import React, { useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useGamePass } from './GamePassProvider';
import { AuthModalProps } from '../types';
import '../styles/modal.css';

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  title = 'Welcome to GamePass',
  imageUrl,
  primaryColor = '#6366f1',
  secondaryColor = '#4f46e5',
  textColor = '#ffffff',
}) => {
  const { onSuccess, onError } = useGamePass();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          }
        );
        const userInfo = await userInfoResponse.json();
        onSuccess?.(userInfo);
        onClose();
      } catch (error) {
        onError?.(error as Error);
      }
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });

  if (!isOpen) return null;

  return (
    <div className="gamepass-overlay" onClick={onClose}>
      <div
        className="gamepass-modal"
        onClick={(e) => e.stopPropagation()}
        style={
          {
            '--primary-color': primaryColor,
            '--secondary-color': secondaryColor,
            '--text-color': textColor,
          } as React.CSSProperties
        }
      >
        <button className="gamepass-close" onClick={onClose}>
          ×
        </button>

        <div className="gamepass-content">
          {imageUrl && (
            <div className="gamepass-image-container">
              <img src={imageUrl} alt="GamePass" className="gamepass-image" />
            </div>
          )}

          <h2 className="gamepass-title">{title}</h2>
          <p className="gamepass-subtitle">Sign in to continue your gaming journey</p>

          <button
            className="gamepass-google-btn"
            onClick={() => login()}
            style={{
              backgroundColor: primaryColor,
              color: textColor,
            }}
          >
            <svg
              className="gamepass-google-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
            >
              <path
                fill="#ffffff"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#ffffff"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#ffffff"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#ffffff"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          <div className="gamepass-divider">
            <span>or</span>
          </div>

          <form className="gamepass-form">
            <input
              type="email"
              placeholder="Email address"
              className="gamepass-input"
              style={{ borderColor: primaryColor }}
            />
            <input
              type="password"
              placeholder="Password"
              className="gamepass-input"
              style={{ borderColor: primaryColor }}
            />
            <button
              type="submit"
              className="gamepass-submit-btn"
              style={{
                backgroundColor: primaryColor,
                color: textColor,
              }}
            >
              Sign Up
            </button>
          </form>

          <p className="gamepass-terms">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>

        <div className="gamepass-footer">
          <span className="gamepass-powered">Powered by GamePass</span>
        </div>
      </div>
    </div>
  );
};